import { useLazyQuery, useMutation, useQuery } from "@apollo/client";
import { CREATE_CONTAINER_COST } from "graphql/mutations";
import { FETCH_CONTAINERS, FETCH_LOADING_PLAN } from "graphql/query";
import { useEffect, useState } from "react";
import { CiCalculator1 } from "react-icons/ci";
import { FaArrowRightLong } from "react-icons/fa6";
import { MdRestartAlt } from "react-icons/md";
import Button, { CharlesButtonWithArrow } from "../base/Button";
import Errors from "../base/Errors";
import { Input, Select } from "../base/Form";
import Spinner, { InlineSpinner } from "../base/Spinner";
import { Alert } from "../base/Toast";

export const shippingZones = ["US", "EU"];

export const getContainers = (planContainers, totalCbm) => {
  // loadingPlan like: {"20GP": 0, "40GP": 1, "40HQ": 2, "45HQ": 0}
  // need to convert it to [ {name: "40GP", cbm: 58, cost: 10000, emptySpace: 0}, {name: "40GP", cbm: 58, cost: 10000, emptySpace: 0}, ...]
  let remainingCbm = totalCbm;
  let totalCapacity = 0;

  const containers = planContainers.flatMap((container) => {
    const containerArray = Array(container.qty).fill();

    const containerObjects = containerArray.map(() => {
      totalCapacity += container.cbm;

      if (remainingCbm >= container.cbm) {
        remainingCbm -= container.cbm;
        return {
          name: container.name,
          cbm: container.cbm,
          cost: container.cost,
          emptySpace: 0,
        };
      } else {
        const emptySpace = container.cbm - remainingCbm;
        remainingCbm = 0;
        return {
          name: container.name,
          cbm: container.cbm,
          cost: container.cost,
          emptySpace,
        };
      }
    });

    return containerObjects;
  });

  // sort the containers by cbm from small to large
  containers.sort((a, b) => a.cbm - b.cbm);

  return { containers, remainingCapacity: totalCapacity - totalCbm };
};

export const getContainerTypes = (planContainers, containers) =>
  planContainers
    .filter((container) => container.qty > 0)
    .map((container) => {
      const emptySpace = containers
        .filter((item) => item.name === container.name)
        .reduce((total, item) => total + item.emptySpace, 0);

      return { ...container, emptySpace: emptySpace / container.qty };
    });

const ContainerizationView = ({
  totalCbm,
  shippingZone,
  defaultDisabledContainers = [],
  summaryFirst = false,
}) => {
  const roundedTotalCbm = totalCbm.toFixed(2);
  const hasDisabledContainers = defaultDisabledContainers.length > 0;
  const defaultSpecificContainers = hasDisabledContainers
    ? JSON.stringify(
        defaultDisabledContainers.reduce((acc, containerName) => {
          acc[containerName] = 0;
          return acc;
        }, {}),
      )
    : null;

  const { loading, error, data } = useQuery(FETCH_LOADING_PLAN, {
    variables: {
      totalCbm: roundedTotalCbm,
      specificContainers: defaultSpecificContainers,
      rate: 1,
      zone: shippingZone,
    },
  });
  const [showCalculator, setShowCalculator] = useState(false);

  if (loading)
    return (
      <div className="relative flex">
        <InlineSpinner size={21} text={null} />
      </div>
    );
  if (error) return <Errors error={error} />;

  const defaultPlanNotes = (
    <ul className="list-disc space-y-2 pl-5 text-sm text-gray-600 dark:text-gray-300">
      <li>
        The default plan is the <strong>best plan</strong> with a container
        <strong> utilization rate of 100%</strong>.
      </li>
      <li>
        The algorithm is trying to find the <strong>lowest cost first</strong>, then the
        smallest total container capacity (the highest possible loading rate).
      </li>
    </ul>
  );

  return (
    <div>
      {summaryFirst ? (
        <div className="space-y-6">
          <div className="flex flex-col gap-6 lg:flex-row lg:items-center">
            <LoadingPlanInfo
              planContainers={data.loadingPlan}
              totalCbm={roundedTotalCbm}
              shippingZone={shippingZone}
            />
            <div className="space-y-4">
              {defaultPlanNotes}
              <CharlesButtonWithArrow
                onClick={() => setShowCalculator(!showCalculator)}
                show={showCalculator}
              >
                <div className="flex items-center">
                  <CiCalculator1 className="text-xl" />
                  Loading Plan Calculator
                </div>
              </CharlesButtonWithArrow>
            </div>
          </div>
          {showCalculator && (
            <Calculator
              defaultTotalCbm={roundedTotalCbm}
              shippingZone={shippingZone}
            />
          )}
        </div>
      ) : (
        <LoadingPlanInfo
          planContainers={data.loadingPlan}
          totalCbm={roundedTotalCbm}
          shippingZone={shippingZone}
        />
      )}

      {!summaryFirst && <div className="mt-4">{defaultPlanNotes}</div>}

      {!summaryFirst && (
        <div className="mt-4 space-y-2">
          <CharlesButtonWithArrow
            onClick={() => setShowCalculator(!showCalculator)}
            show={showCalculator}
          >
            <div className="flex items-center">
              <CiCalculator1 className="text-xl" />
              Loading Plan Calculator
            </div>
          </CharlesButtonWithArrow>

          {showCalculator && (
            <Calculator
              className="mt-2"
              defaultTotalCbm={roundedTotalCbm}
              shippingZone={shippingZone}
            />
          )}
        </div>
      )}
    </div>
  );
};

const LoadingPlanInfo = ({
  planContainers,
  totalCbm,
  shippingZone,
}) => {
  const totalCost = planContainers.reduce(
    (acc, planContainer) => acc + planContainer.cost * planContainer.qty,
    0,
  );
  const totalCapacity = planContainers.reduce(
    (acc, planContainer) => acc + planContainer.cbm * planContainer.qty,
    // acc + planContainer.cbm * (rate / 100) * planContainer.qty,
    0,
  );
  const containerTypes = getContainerTypes(
    planContainers,
    getContainers(planContainers, totalCbm).containers,
  );

  return (
    <div className="w-full rounded-xl border border-gray-200 p-4 dark:border-gray-700 lg:w-fit">
      <div className="flex flex-col text-sm lg:flex-row">
        <div className="space-y-2 lg:self-center">
          <div className="flex">
            <label className="w-28">Total Cost: </label>
            <span className="font-bold">
              {totalCost} $
            </span>
          </div>
          <div className="flex">
            <label className="w-28">Total CBM: </label>
            <span className="font-bold">{totalCbm} m³</span>
          </div>
          <div className="flex">
            <label className="w-28">Empty Space: </label>
            <span className="font-bold">
              {(totalCapacity - totalCbm).toFixed(2)} m³
            </span>
          </div>
          <div className="flex">
            <label className="w-28">Loading Rate: </label>
            <span className="font-bold">
              {((totalCbm / totalCapacity) * 100).toFixed(2)} %
            </span>
          </div>
          <div className="flex">
            <label className="w-28">Shipping Zone: </label>
            <span className="font-bold">{shippingZone}</span>
          </div>
        </div>

        <div className="my-4 border-t border-dashed border-gray-200 dark:border-gray-700 lg:mx-8 lg:my-0 lg:border-l lg:border-t-0" />

        <div className="flex flex-wrap gap-4">
          {containerTypes.map((container) => (
            <ContainerView
              container={container}
              key={container.name}
              quantity={container.qty}
              showDetails
            />
          ))}
        </div>
      </div>
    </div>
  );
};

const ContainerView = ({ container, quantity, showDetails = false }) => {
  const loadingPercentage = (1 - container.emptySpace / container.cbm) * 100;
  const width = 2 * container.cbm; // width is based on the cbm of the container, 1 cbm = 2px here, just for better visualization

  return (
    <div className={showDetails ? "py-0" : "py-4"}>
      <div className="flex items-center gap-3">
        <div
          className="w-16 h-24 relative bg-gray-100 border-2 border-gray-400"
          style={{ width }}
        >
          <div
            className="w-full bg-green-500 absolute bottom-0 transition-all duration-500 ease-in-out"
            style={{
              height: `${loadingPercentage}%`,
            }}
          />
          {showDetails && (
            <span className="absolute inset-0 z-10 flex items-center justify-center text-base font-bold">
              {container.name}
            </span>
          )}
        </div>
        {showDetails && <span className="text-lg font-bold">&times; {quantity}</span>}
      </div>
      {showDetails ? (
        <div
          className="mt-1 text-center text-sm text-gray-600 dark:text-gray-300"
          style={{ width }}
        >
          <div>
            <span className="font-bold">{container.cbm}</span>
            <span> m³</span>
          </div>
          <div>
            <span className="font-bold">{container.cost}</span>
            <span> $</span>
          </div>
        </div>
      ) : (
        <div className="mt-1 text-center font-bold">{container.name}</div>
      )}
    </div>
  );
};

const ResetButton = ({ onClick, label }) => (
  <button
    aria-label={label}
    className="flex h-6 w-6 items-center justify-center rounded text-gray-600 transition hover:bg-gray-200 hover:text-gray-900 dark:text-gray-300 dark:hover:bg-gray-700 dark:hover:text-gray-100"
    onClick={onClick}
    title={label}
    type="button"
  >
    <MdRestartAlt className="text-lg" />
  </button>
);

const Calculator = ({ defaultTotalCbm, shippingZone }) => {
  const [totalCBM, setTotalCBM] = useState(defaultTotalCbm);
  const [specificContainers, setSpecificContainers] = useState({});
  const [rate, setRate] = useState(100);
  const [zone, setZone] = useState(shippingZone);

  const {
    loading: containerLoading,
    error: containerError,
    data: containerData,
  } = useQuery(FETCH_CONTAINERS, { variables: { zone: zone } });

  const [fetchLoadingPlan, { loading, error, data }] =
    useLazyQuery(FETCH_LOADING_PLAN);

  const [finalLoadingPlan, setFinalLoadingPlan] = useState(null);

  useEffect(() => {
    if (data) {
      const result = data.loadingPlan.map((p) => p);

      // add the specific containers to the result
      Object.entries(specificContainers).forEach(([name, qty]) => {
        containerData.containers.forEach((c) => {
          if (c.name === name) {
            result.push({ ...c, qty });
          }
        });
      });

      setFinalLoadingPlan(result);
    }
  }, [data]);

  useEffect(() => {
    // clean plan when calculation conditions change
    setFinalLoadingPlan(null);
  }, [totalCBM, rate, specificContainers, zone]);

  const onChangeQty = (name, value) => {
    setSpecificContainers((prev) => {
      if (value === "") {
        const { [name]: _, ...newSpecificContainers } = prev;
        return newSpecificContainers;
      }

      return { ...prev, [name]: parseInt(value) };
    });
  };

  const handleCalculate = () => {
    fetchLoadingPlan({
      variables: {
        totalCbm: totalCBM,
        specificContainers: JSON.stringify(specificContainers),
        rate: rate / 100,
        zone: zone,
      },
    });
  };

  if (containerLoading)
    return (
      <div className="relative">
        <InlineSpinner size={21} />
      </div>
    );
  if (containerError) return <Errors errors={containerError} />;

  return (
    <div className="flex flex-col items-stretch lg:flex-row lg:items-center">
      <div className="w-full space-y-4 rounded-xl bg-gray-100 p-4 text-sm shadow-lg dark:bg-gray-900 lg:w-fit">
        <h5 className="text-base font-semibold">Loading Plan Calculator</h5>
        <div className="space-y-2">
          <div className="flex flex-wrap items-center gap-2">
            <label className="w-full lg:w-48">Total CBM (m³)</label>
            <Input
              type="number"
              className="w-24"
              min={0}
              value={totalCBM}
              onChange={(e) => setTotalCBM(parseFloat(e.target.value))}
            />
            <ResetButton
              label="Reset total CBM"
              onClick={() => setTotalCBM(defaultTotalCbm)}
            />
          </div>

          <div className="flex flex-wrap items-center gap-2">
            <label className="w-full lg:w-48">Utilization Rate (%)</label>
            <Input
              className="w-24"
              type="number"
              min={0}
              max={100}
              value={rate}
              onChange={(e) => setRate(parseFloat(e.target.value))}
            />
            <ResetButton
              label="Reset utilization rate"
              onClick={() => setRate(100)}
            />
          </div>

          <div className="flex flex-wrap items-center gap-2">
            <label className="w-full lg:w-48">Shipping Zone</label>
            <Select
              value={zone}
              onChange={(e) => setZone(e.target.value)}
              className="w-24 text-xs"
            >
              {shippingZones.map((z, index) => (
                <option key={index} value={z}>
                  {z}
                </option>
              ))}
            </Select>
            <ResetButton
              label="Reset shipping zone"
              onClick={() => setZone(shippingZone)}
            />
          </div>
        </div>

        <div className="space-y-3 border-t border-dashed border-gray-200 pt-4 dark:border-gray-700">
          {containerData.containers.map((c, index) => (
            <div key={index} className="flex flex-wrap items-center gap-2">
              <div className="w-full lg:w-48">
                <span className="font-bold">{c.name}</span>
                <span className="ml-2">
                  (
                  <span className="font-bold">{c.cbm}</span>
                  <span className="text-gray-600 dark:text-gray-300"> m³</span>
                  <span className="mx-1 text-gray-600 dark:text-gray-300">·</span>
                  <span className="text-gray-600 dark:text-gray-300">$</span>
                  <span className="font-bold">{c.cost}</span>)
                </span>
              </div>

              <Input
                className="w-24"
                placeholder="unlimited"
                type="number"
                min={0}
                value={
                  specificContainers[c.name] !== undefined &&
                  specificContainers[c.name] !== null
                    ? specificContainers[c.name]
                    : ""
                }
                onChange={(e) => onChangeQty(c.name, e.target.value)}
              />

              <ResetButton
                label={`Clear ${c.name} limit`}
                onClick={() => onChangeQty(c.name, "")}
              />
            </div>
          ))}
        </div>

        <div className="flex justify-end border-t border-gray-200 pt-4 dark:border-gray-700">
          <Button
            className="rounded-md px-2 py-1 text-sm transition-colors hover:bg-sky-100 active:bg-sky-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-sky-400 dark:hover:bg-sky-900/60 dark:active:bg-sky-800/70"
            onClick={handleCalculate}
          >
            Calculate plan
          </Button>
        </div>
      </div>

      {loading && (
        <div className="flex-1">
          <InlineSpinner size={21} text="Calculating..." />
        </div>
      )}
      {error && <Errors error={error} />}

      {finalLoadingPlan && (
        <>
          <FaArrowRightLong className="my-6 self-center rotate-90 text-2xl lg:mx-6 lg:my-0 lg:rotate-0" />

          <LoadingPlanInfo
            planContainers={finalLoadingPlan}
            totalCbm={totalCBM}
            shippingZone={zone}
          />
        </>
      )}
    </div>
  );
};

export const ContainerCostForm = ({ hide }) => {
  const [zone, setZone] = useState("US");
  const [containers, setContainers] = useState([]);

  const [createContainerCost] = useMutation(CREATE_CONTAINER_COST, {
    refetchQueries: [{ query: FETCH_CONTAINERS, variables: { zone: zone } }],
    onCompleted() {
      Alert("success", "Successfully created container costs");
      hide();
    },
    onError(error) {
      Alert("error", error.message);
    },
  });
  const { loading, error, data } = useQuery(FETCH_CONTAINERS, {
    variables: { zone: zone },
  });

  const handleInputChange = (newCost, container) => {
    const newContainers = [...containers];

    if (newCost === "") {
      // ignore the container if the new cost is empty
      const updatedContainers = newContainers.filter(
        (c) => c.name !== container.name,
      );
      setContainers(updatedContainers);
    } else {
      const cost = parseInt(newCost);
      const existingIndex = newContainers.findIndex(
        (c) => c.name === container.name,
      );

      // add the new container or update the cost of the existing container
      if (existingIndex === -1) {
        newContainers.push({
          name: container.name,
          cbm: container.cbm,
          cost: cost,
          zone: container.zone,
        });
      } else {
        newContainers[existingIndex] = {
          ...newContainers[existingIndex],
          cost: cost,
        };
      }

      setContainers(newContainers);
    }
  };

  useEffect(() => {
    setContainers([]);
  }, [zone]);

  if (loading) return <Spinner />;
  if (error) return <Errors error={error} />;

  return (
    <div className="space-y-4">
      <div className="flex space-x-2 items-center">
        <label>Shipping Zone:</label>
        <Select
          value={zone}
          onChange={(e) => setZone(e.target.value)}
          className="w-24 text-xs"
        >
          {shippingZones.map((z, index) => (
            <option key={index} value={z}>
              {z}
            </option>
          ))}
        </Select>
      </div>

      <div className="border border-gray-200 border-dashed" />

      {data.containers.map((c, index) => (
        <div key={index} className="flex items-center ">
          <div className="w-2 h-2 bg-sky-400 rounded-full mr-2" />

          <div className="flex items-center justify-between w-full">
            <div className="flex items-center">
              <span className="font-bold">{c.name}</span>
              <span className="ml-2">({c.cbm} m³)</span>
            </div>

            <div className="flex items-center">
              <div>
                <span className="font-bold">{c.cost}</span>
                <span className="ml-2">USD</span>
              </div>

              <FaArrowRightLong className="text-xs mx-6" />

              <div>
                <Input
                  className="w-24"
                  type="number"
                  min={0}
                  value={
                    containers.find((container) => container.name === c.name)
                      ?.cost || ""
                  }
                  onChange={(e) => handleInputChange(e.target.value, c)}
                />
                <span className="ml-2">USD</span>
              </div>
            </div>
          </div>
        </div>
      ))}

      <div className="flex items-center space-x-2">
        <Button
          onClick={() => {
            createContainerCost({
              variables: { containerInputs: containers, zone: zone },
            });
          }}
          className="border border-sky-300 text-sky-500 hover:text-sky-800 hover:bg-sky-500 hover:border-sky-500 active:border-sky-700 text-xs px-2 py-1 rounded-md"
        >
          Save
        </Button>

        <Button
          onClick={hide}
          className="text-xs px-2 py-1 rounded-md text-gray-400"
        >
          Cancel
        </Button>
      </div>
    </div>
  );
};

export default ContainerizationView;
